/**
 * Valores iniciais da simulação de ICMS Passivo.
 *
 * ## Preferência do navegador, não configuração da conta
 *
 * Deságio, parcelas, juros e sistema de amortização são o ponto de partida da
 * simulação — o número que aparece no campo antes de alguém digitar. Não existe
 * tabela de configurações da divisão no banco: o Passivo guarda esses valores
 * **por contrato** (`ContratoPassivo`), e criar uma tabela só para o ponto de
 * partida de uma tela seria schema para uma preferência de tela.
 *
 * Por isso ficam em `localStorage`, na chave abaixo. A consequência é conhecida
 * e aceita: outro navegador, outra máquina ou uma limpeza de dados voltam ao
 * padrão de fábrica. Quando houver configuração de conta no servidor, esta
 * função troca a fonte e a simulação não muda.
 *
 * ## Por que os campos são texto
 *
 * A simulação guarda o formulário como texto e só converte no envio. Devolver
 * texto aqui evita converter duas vezes e manter duas representações do mesmo
 * valor — e a leitura já normaliza o decimal para ponto, que é o que `Number()`
 * entende.
 */

export const CHAVE_DEFAULTS_PASSIVO = 'deca.passivo.defaults';

export type SistemaDeAmortizacao = 'sac' | 'price';

export interface DefaultsDaSimulacao {
  readonly percentualDesagio: string;
  readonly quantidadeParcelas: string;
  readonly taxaJurosMensal: string;
  readonly indice: SistemaDeAmortizacao;
}

/** O que a tela usava antes de ser configurável — continua sendo o fallback. */
export const DEFAULTS_DE_FABRICA: DefaultsDaSimulacao = {
  percentualDesagio: '25',
  quantidadeParcelas: '12',
  taxaJurosMensal: '1',
  indice: 'sac',
};

/**
 * Lê os defaults gravados, campo a campo.
 *
 * Um registro antigo (chave a menos) ou corrompido não derruba a tela: o campo
 * que faltar ou for inválido cai para o de fábrica, e os demais valem. Validar
 * campo a campo, em vez de o objeto inteiro, é o que faz uma versão futura com
 * um campo novo não zerar o que o usuário já tinha escolhido.
 */
export function lerDefaults(
  armazenamento: Pick<Storage, 'getItem'> | null = armazenamentoDoNavegador(),
): DefaultsDaSimulacao {
  if (armazenamento === null) return DEFAULTS_DE_FABRICA;

  let bruto: unknown;
  try {
    const texto = armazenamento.getItem(CHAVE_DEFAULTS_PASSIVO);
    if (texto === null) return DEFAULTS_DE_FABRICA;
    bruto = JSON.parse(texto);
  } catch {
    return DEFAULTS_DE_FABRICA;
  }

  if (typeof bruto !== 'object' || bruto === null) return DEFAULTS_DE_FABRICA;
  const r = bruto as Record<string, unknown>;

  return {
    percentualDesagio:
      numeroDentro(r['percentualDesagio'], 0, 100) ?? DEFAULTS_DE_FABRICA.percentualDesagio,
    quantidadeParcelas:
      inteiroDentro(r['quantidadeParcelas'], 1, 360) ?? DEFAULTS_DE_FABRICA.quantidadeParcelas,
    // RN-215 — a taxa mensal vale entre 0 e 10; fora disso a API recusa, e um
    // default recusado seria um formulário que nasce errado.
    taxaJurosMensal:
      numeroDentro(r['taxaJurosMensal'], 0, 10) ?? DEFAULTS_DE_FABRICA.taxaJurosMensal,
    indice:
      r['indice'] === 'price' || r['indice'] === 'sac' ? r['indice'] : DEFAULTS_DE_FABRICA.indice,
  };
}

/** Grava os defaults. Devolve `false` quando o navegador recusa (modo privado, cota). */
export function gravarDefaults(
  defaults: DefaultsDaSimulacao,
  armazenamento: Pick<Storage, 'setItem'> | null = armazenamentoDoNavegador(),
): boolean {
  if (armazenamento === null) return false;
  try {
    armazenamento.setItem(CHAVE_DEFAULTS_PASSIVO, JSON.stringify(defaults));
    return true;
  } catch {
    return false;
  }
}

export function limparDefaults(
  armazenamento: Pick<Storage, 'removeItem'> | null = armazenamentoDoNavegador(),
): void {
  if (armazenamento === null) return;
  try {
    armazenamento.removeItem(CHAVE_DEFAULTS_PASSIVO);
  } catch {
    // Sem armazenamento não há o que limpar.
  }
}

/* ─── Auxiliares ──────────────────────────────────────────────────────────── */

/**
 * O acesso a `localStorage` pode lançar (navegador com dados de site
 * bloqueados) — e em teste não existe `window`. Nos dois casos a resposta é
 * "sem armazenamento", nunca uma exceção na renderização.
 */
function armazenamentoDoNavegador(): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Aceita número ou texto numérico (com vírgula ou ponto) dentro da faixa e
 * devolve o texto com ponto decimal — é o que a simulação converte com
 * `Number()` na hora de enviar; vírgula ali viraria `NaN`.
 */
function numeroDentro(valor: unknown, minimo: number, maximo: number): string | null {
  const n = paraNumero(valor);
  if (n === null || n < minimo || n > maximo) return null;
  return String(n);
}

function inteiroDentro(valor: unknown, minimo: number, maximo: number): string | null {
  const n = paraNumero(valor);
  if (n === null || !Number.isInteger(n) || n < minimo || n > maximo) return null;
  return String(n);
}

function paraNumero(valor: unknown): number | null {
  if (typeof valor === 'number') return Number.isFinite(valor) ? valor : null;
  if (typeof valor !== 'string' || valor.trim() === '') return null;
  const n = Number(valor.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}
